/**
 * Story `fe-be-monteur-s5b-installeerbaar` (EPIC-021, S021.5b) §3.2 — één regel: welk
 * inlogscherm hoort bij dit pad.
 *
 * De monteur-app heeft een eigen inlogscherm BINNEN zijn scope (`/monteur/inloggen`, vondst
 * 3): in een geïnstalleerde app toont Android anders een adresbalk bij het inloggen. Alle
 * plekken die naar een inlogscherm sturen gebruiken deze functies: `ProtectedRoute`,
 * `AuthProvider` (de `carculate:unauthenticated`-afhandeling), het uitloggen in de schil en
 * de inlog-link op het fotoscherm.
 *
 * <b>Eén helper `isFitterPath`</b> (review A-7): de scope is een TEKST-prefix (`/monteur`), geen
 * padsegment. `startsWith('/monteur')` zou `/monteurs`, `/monteur-sw.js` en
 * `/monteur-icons/…` ook als monteur-pad zien. Hier telt alleen `/monteur` zelf en alles
 * onder `/monteur/`. De service worker heeft een eigen kopie (hij kan niet uit `src/`
 * importeren); een test vergelijkt beide over dezelfde tabel (TC-S-FE-05).
 *
 * Staat in `src/auth/` (review H-8), naast `ProtectedRoute`: die laadt toch al in de entry.
 */

/** Het adres van de monteur-weergave (zelfde waarde als `FITTER_HOME`). */
export const FITTER_ROOT_PATH = '/monteur';

/** Het inlogscherm van de monteur-app (concept 1a). */
export const FITTER_LOGIN_PATH = '/monteur/inloggen';

/** Het inlogscherm van de kantoor-app. */
export const OFFICE_LOGIN_PATH = '/login';

/**
 * Story `be-fe-wachtwoord-vergeten` (AC 1, 8, 19) — de vier publieke schermen van "wachtwoord vergeten". Kantoor
 * zonder prefix, monteur onder `/monteur/` (monteur-URL's zijn Nederlands, W-2). De paden waar de mail naar linkt
 * (`…/wachtwoord-herstellen`) bepaalt de server; deze waarden moeten daar gelijk aan zijn (P-11).
 */
export const OFFICE_FORGOT_PASSWORD_PATH = '/wachtwoord-vergeten';
export const OFFICE_RESET_PASSWORD_PATH = '/wachtwoord-herstellen';
export const FITTER_FORGOT_PASSWORD_PATH = '/monteur/wachtwoord-vergeten';
export const FITTER_RESET_PASSWORD_PATH = '/monteur/wachtwoord-herstellen';

/**
 * Is dit een van de publieke "wachtwoord vergeten"-schermen? Daar stuurt een 401 de gebruiker niet naar het
 * inlogscherm en wordt er niet hervat: die schermen werken juist zonder (of naast) een sessie (P-17).
 */
export function isPasswordResetPath(pathname: string | null | undefined): boolean {
    if (!pathname) return false;
    const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    return (
        p === OFFICE_FORGOT_PASSWORD_PATH ||
        p === OFFICE_RESET_PASSWORD_PATH ||
        p === FITTER_FORGOT_PASSWORD_PATH ||
        p === FITTER_RESET_PASSWORD_PATH
    );
}

/** Is dit een pad van de monteur-app? Alleen `/monteur` en `/monteur/…` (hoofdlettergevoelig, zoals de router). */
export function isFitterPath(pathname: string | null | undefined): boolean {
    if (!pathname) return false;
    return pathname === FITTER_ROOT_PATH || pathname.startsWith(`${FITTER_ROOT_PATH}/`);
}

/** Het inlogscherm voor dit pad: `/monteur/inloggen` binnen de monteur-app, anders `/login`. */
export function loginPathFor(pathname: string | null | undefined): string {
    return isFitterPath(pathname) ? FITTER_LOGIN_PATH : OFFICE_LOGIN_PATH;
}

/** Is dit zelf een inlogscherm? Dan hoort het nooit in `state.from` (review H-4). */
export function isLoginPath(pathname: string | null | undefined): boolean {
    if (!pathname) return false;
    return pathname === OFFICE_LOGIN_PATH || pathname === FITTER_LOGIN_PATH || pathname === `${FITTER_LOGIN_PATH}/`;
}

/** De vorm van `state.from` die `ProtectedRoute` en `AuthProvider` meegeven. */
export interface LoginFrom {
    pathname?: string;
    search?: string;
    hash?: string;
}

/**
 * §3.1 / AC 14 — waar de monteur na het inloggen heen gaat: naar `state.from` als dat een
 * monteur-pad is (en geen inlogscherm), anders naar `/monteur`. `search` en `hash` gaan mee
 * (review H-5: een deep link met `?` of `#` blijft heel). Alles wat geen object met een
 * `pathname` is — `null`, een string, een ander domein (`//elders.nl`) — geeft `/monteur`.
 */
export function fitterReturnPath(from: unknown): string {
    if (!from || typeof from !== 'object') return FITTER_ROOT_PATH;
    const f = from as LoginFrom;
    const pathname = typeof f.pathname === 'string' ? f.pathname : '';
    if (!isFitterPath(pathname) || isLoginPath(pathname)) return FITTER_ROOT_PATH;
    const search = typeof f.search === 'string' && f.search.startsWith('?') ? f.search : '';
    const hash = typeof f.hash === 'string' && f.hash.startsWith('#') ? f.hash : '';
    return `${pathname}${search}${hash}`;
}
